import {
  ActionIcon, Badge, Box, Button, Card, Center, Checkbox, Collapse, Group, rem,
  SegmentedControl, SimpleGrid, Stack, Text, Textarea
} from "@mantine/core";
import {
  IconBookUpload, IconBulb, IconChevronDown, IconChevronUp, IconDeviceFloppy,
  IconEdit, IconInfoCircle, IconPlus, IconTargetArrow, IconTools, IconTrash,
  IconUsers
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ATTRIBUTE_TYPES } from "../../enums/AttributesTypes";
import TitleWithDescription from "../TitleWithDescription";
import { useSelector, useDispatch } from "react-redux";
import { saveStrategy, saveGoal, saveResource, saveResponsible, deleteStrategy, deleteGoal, deleteResponsible, deleteResource } from "../../actions/improvementPlanActions";
import ModalEditorGoal from "./ModalEditorGoal";
import ModalEditorResource from "./ModalEditorResource";
import ModalEditorResponsible from "./ModalEditorResponsible";
import ModalDialog from '../ModalDialog'
import { formatDate } from '../../utils/dateUtils'
import { usePlanFlags } from "../../hooks/usePlanFlags";

const mergeSelections = (currentIds = [], newSelectedIds = [], optionsOfType = []) => {
  const valid = new Set(optionsOfType.map(o => o.Id));
  return [...currentIds.filter(id => !valid.has(id)), ...newSelectedIds.filter(valid.has, valid)];
};

const normalizeObjective = (obj) => ({
  ...(obj ?? { cualidades: [] }),
  cualidades: (obj?.cualidades ?? []).map(c => (typeof c === 'string' ? c : c?.Id)).filter(Boolean),
});

const TABS = [
  {
    value:'Metas', 
    label: (
      <Center style={{ gap: 10 }}>
        <IconTargetArrow size={16} /><span>Metas</span>
      </Center>
    ),
  }, 
  {
    value:'Recursos', 
    label: (
      <Center style={{ gap: 10 }}>
        <IconTools size={16} /><span>Recursos</span>
      </Center>
    ),
  }, 
  {
    value:'Responsables', 
    label: (
      <Center style={{ gap: 10 }}>
        <IconUsers size={16} /><span>Responsables</span>
      </Center>
    ),
  },
];

const ObjectiveCard = ({evaluationId, canEdit=false, objective, index, attributes, onSave, onComplete, onDelete }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { obj, temp, is } = usePlanFlags(objective?.Id);

  const [open, setOpen] = useState(false);
  const [objectiveData, setObjectiveData] = useState(() => normalizeObjective(objective));
  const [openStrategyId, setOpenStrategyId] = useState(null);
  const [activeTabByStrategy, setActiveTabByStrategy] = useState({});

  const [completeOpen, setCompleteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [strategyModal, setStrategyModal] = useState({
    open: false,
    tab: null,
    strategyId: null,
    item: null,
  });

  const isCompleted = !!objective?.completado;
  const weaknessOptions = attributes?.filter((a) => a.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.DEBILIDAD);
  const strengthsOptions = attributes?.filter((a) => a.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.FORTALEZA);

  const toggleStrategy = useCallback((id) => {
    setOpenStrategyId((curr) => (curr === id ? null : id));
  }, []);

  const getActiveTab = useCallback((id) => activeTabByStrategy[id] ?? 'Metas', [activeTabByStrategy]);
  const setActiveTab = useCallback((id, tab) => { setActiveTabByStrategy(s => (s[id] === tab ? s : { ...s, [id]: tab })); }, []);

  const lock = useMemo(() => {
    const completed = isCompleted;
    const editable = Boolean(canEdit && !completed);
    const readOnly = !editable;

    const busy = obj?.saving || obj?.deleting || obj?.completing;

    return {
      completed,
      editable,
      readOnly,
      busy,
      cardBg: completed ? '#f6fff7' : '#fffcf4',
      badge: completed
        ? { color: 'green', label: 'Publicado' }
        : { color: 'yellow', label: 'En Edición' },
    };
  }, [canEdit, isCompleted, obj?.saving, obj?.deleting, obj?.completing]);
  
  useEffect(() => {
    if(objective){
      setObjectiveData(normalizeObjective(objective));
    }
  }, [objective]);

  const handleSaveObjective = (obj) => {
    const ids = Array.isArray(obj.cualidades)
      ? obj.cualidades
          .map(x => (typeof x === 'string' ? x : x?.Id))
          .filter(Boolean)
      : [];

    const payload = {
      ...obj,
      cualidades: ids.map(Id => ({ Id })),
    };

    onSave(payload);
  };

  const handleObjectiveDescription = (newValue) => {
    setObjectiveData((prev) => ({ ...prev, descripcion: newValue }));
  };

  const handleGroupChange = (type, optionsOfType) => (selectedIds) => {
    setObjectiveData(prev => {
      const current = prev?.cualidades ?? [];
      const nextCualidades = mergeSelections(current, selectedIds, optionsOfType);
      return { ...prev, cualidades: nextCualidades };
    });
  };

  const handleAddStrategy = (objectiveId) => {
    handleSaveObjective(objectiveData);
    const newStrategy = { descripcion: "", metas: [], recursos: [], responsables: [] };
    dispatch(saveStrategy(newStrategy, objectiveId, evaluationId, user));
  };

  const handleDeleteStrategy = (strategyId) => {
    dispatch(deleteStrategy(strategyId, evaluationId, user));
  };

  const handleStrategyDescription = (strategyId, newValue) => {
    setObjectiveData(prev => ({
      ...prev,
      estrategias: prev.estrategias?.map(est =>
        est.Id === strategyId ? { ...est, descripcion: newValue } : est
      )
    }));
  };

  const openCreateModal = (strategyId, tab) => {
    setStrategyModal({ open: true, tab, strategyId, item: null });
  };
  const openEditModal = (strategyId, tab, item) => {
    setStrategyModal({ open: true, tab, strategyId, item });
  };
  const handleModalClose = () => {
    setStrategyModal({ open: false, tab: null, strategyId: null, item: null });
  };

  const handleSaveStrategyElement = (elementData) => {
    const { strategyId, tab } = strategyModal;
    if (!elementData) return;

    // Primero guardar la estrategia con sus cambios actuales
    const strategy = objectiveData.estrategias?.find(est => est.Id === strategyId);
    if (!strategy) return;

    const savePromise = dispatch(saveStrategy(strategy, objectiveData.Id, evaluationId, user));

    // Luego guardar el elemento (goal, resource o responsible)
    savePromise.then(() => {
      if (tab === 'Metas') {
        dispatch(saveGoal(elementData, strategyId, evaluationId, user)).then(() => handleModalClose());
      } else if (tab === 'Recursos') {
        dispatch(saveResource(elementData, strategyId, evaluationId, user)).then(() => handleModalClose());
      } else if (tab === 'Responsables') {
        dispatch(saveResponsible(elementData, strategyId, evaluationId, user)).then(() => handleModalClose());
      }
    });
  };

  const handleDeleteGoal = (strategyId, goalId) => {
    dispatch(deleteGoal(goalId, strategyId, evaluationId, user));
  };
  const handleDeleteResource = (strategyId, resourceId) => {
    dispatch(deleteResource(resourceId, evaluationId, user));
  };
  const handleDeleteResponsible = (strategyId, responsibleId) => {
    dispatch(deleteResponsible(responsibleId, evaluationId, user));
  };

  const handleConfirmComplete = (objective) => {
    const ids = Array.isArray(objective.cualidades)
      ? objective.cualidades
          .map(x => (typeof x === 'string' ? x : x?.Id))
          .filter(Boolean)
      : [];

    const payload = {
      ...objective,
      cualidades: ids.map(Id => ({ Id })),
    };

    onComplete(payload);
    setCompleteOpen(false);
  };

  const handleConfirmDelete = (objectiveId) => {
    onDelete(objectiveId);
    setDeleteOpen(false)
  };

  const renderItems = (items = [], onEdit, onDelete, title, elementType, subtitle) => {
    if (!items.length) return <Text c="dimmed" size="sm" ta="center">No hay elementos cargados</Text>;

    const deletingFn = elementType === 'Meta'
      ? is.goal.deleting
      : elementType === 'Recurso'
      ? is.resource.deleting
      : elementType === 'Responsable'
      ? is.responsible.deleting
      : () => false;

    return items
      .filter(item => item && item.Id) // Filtrar items sin Id
      .map(item => (
      <Card key={item.Id} withBorder radius="sm" p="xs" mb="xs">
        <Group>
          <Stack gap={5}>
            <Text fw={500}>{elementType} - <Text inherit span fw={600}>{title(item)}</Text></Text>
            {subtitle && <Text c="dimmed" size="sm">{subtitle(item)}</Text>}
          </Stack>

          {lock.editable && (
            <Group gap="xs" ml="auto">
              <ActionIcon variant="subtle" onClick={() => onEdit?.(item)}>
                <IconEdit style={{height:'70%',width:'70%'}} />
              </ActionIcon>
              <ActionIcon
                variant="subtle" color="red"
                onClick={() => onDelete?.(item.Id)}
                loading={deletingFn(item.Id)}
                disabled={lock.busy}
              >
                <IconTrash style={{height:'70%',width:'70%'}} />
              </ActionIcon>
            </Group>
          )}
        </Group>
      </Card>
    ));
};

  return (
    <Card withBorder radius="md" p="sm" bg={lock.cardBg}>
      {/* Header */}
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" onClick={() => setOpen((v) => !v)} style={{ cursor: "pointer" }}>
          {!open ? <IconChevronDown /> : <IconChevronUp />}
          <Text fw={600} truncate='end' maw={rem(350)}>
            Objetivo - {objectiveData.descripcion || '[Sin descripción]'}
          </Text>
          <Badge color={lock.badge.color} radius="sm">{lock.badge.label}</Badge>
        </Group>
        <Group wrap="nowrap" gap="sm">
          {lock.editable && (
            <>
              <Button
                variant="default"
                leftSection={<IconDeviceFloppy size={20} />}
                size="compact-md"
                onClick={() => handleSaveObjective(objectiveData)}
                loading={obj.saving}
                disabled={lock.busy}
              >
                Guardar
              </Button>
              <Button 
                variant="default" 
                leftSection={<IconBookUpload size={20} />} 
                size="compact-md" 
                onClick={() => setCompleteOpen(true)}
                loading={obj.completing}
                disabled={lock.busy}
              >
                Publicar
              </Button>
            </>
          )}
          <ActionIcon 
            variant="default" 
            title="Eliminar" 
            onClick={() => setDeleteOpen(true)}
            loading={obj.deleting}
            disabled={lock.busy}
          >
            <IconTrash size={20} />
          </ActionIcon>
        </Group>
      </Group>

      <Collapse in={open}>
        <Stack p="md" gap="md">
          {/* Descripción */}
          <Box>
            {lock.completed ? (
              <Text>{objectiveData.descripcion ?? ''}</Text>
            ):(
              <>
                <Textarea
                  label="Descripción"
                  placeholder="Describa el objetivo de forma clara y específica ..."
                  autosize
                  value={objectiveData.descripcion || ""}
                  onChange={(e) => handleObjectiveDescription(e.target.value)}
                  minRows={2}
                  maxRows={4}
                  maxLength={350}
                  disabled={lock.readOnly}
                />
                <Text ta="end" c="dimmed" size="sm">{(objectiveData.descripcion?.length || 0)}/350</Text>
              </>
            )}
          </Box>

          {/* Debilidades / Fortalezas */}
          <Box>
            <Text fw={500} mb={4}>Seleccione Debilidades y Fortalezas que fundamenten este Objetivo:</Text>
            <SimpleGrid cols={2}>
              <Card withBorder p="xs">
                <Text fw={500}>Debilidades</Text>
                <Checkbox.Group
                  value={objectiveData?.cualidades?.filter(id => weaknessOptions?.some(w => w.Id === id)) ?? []}
                  onChange={handleGroupChange(ATTRIBUTE_TYPES.DEBILIDAD, weaknessOptions ?? [])}
                >
                  {weaknessOptions?.length ? weaknessOptions.map((a) => (
                    <Checkbox 
                      key={a.Id} 
                      value={a.Id} label={a.titulo} 
                      ml={15} mt={5} color="#E72166"
                      styles={{ input: { cursor: 'pointer' }, label: { cursor: 'pointer' } }} 
                      disabled={lock.readOnly}
                    />
                  )) : <Text c="dimmed" size="sm">No hay debilidades cargadas</Text>}
                </Checkbox.Group>
              </Card>
              <Card withBorder p="xs">
                <Text fw={500}>Fortalezas</Text>
                <Checkbox.Group
                  value={objectiveData?.cualidades?.filter(id => strengthsOptions?.some(s => s.Id === id)) ?? []}
                  onChange={handleGroupChange(ATTRIBUTE_TYPES.FORTALEZA, strengthsOptions ?? [])}
                >
                  {strengthsOptions?.length ? strengthsOptions.map((a) => (
                    <Checkbox 
                      key={a.Id} 
                      value={a.Id} label={a.titulo} 
                      ml={15} mt={5} color="#E72166"
                      styles={{ input: { cursor: 'pointer' }, label: { cursor: 'pointer' } }} 
                      disabled={lock.readOnly}
                    />
                  )) : <Text c="dimmed" size="sm">No hay fortalezas cargadas</Text>}
                </Checkbox.Group>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Estrategias */}
          <Box>
            <Group justify="space-between">
              <Text fw={600} my="sm" size="lg">Actividad / Estrategias ({objectiveData.estrategias?.length || 0})</Text>
              {lock.editable && (
                <Button
                  color="#E72166"
                  size="sm"
                  leftSection={<IconPlus style={{ width: '70%', height: '70%' }} />}
                  onClick={() => handleAddStrategy(objectiveData.Id)}
                  loading={temp.strategySaving} disabled={lock.busy}
                >
                  Nueva Estrategia
                </Button>
              )}
            </Group>
            
            {lock.editable && (
              <Card withBorder radius="md" p="lg" style={{ borderLeftColor: '#e72166', borderLeftWidth: '4px' }}>
                <SimpleGrid cols={2}>
                  <TitleWithDescription
                    icon={<IconInfoCircle size={16} />}
                    size="sm"
                    title="Definición de Estrategia"
                    description="Son cursos de acción para alcanzar los objetivos. El número de acciones depende de la complejidad del problema."
                  />
                  <TitleWithDescription
                    icon={<IconBulb size={16} />}
                    size="sm"
                    title="Recomendaciones"
                    description="Cada actividad debe estar claramente definida y cómo contribuye al objetivo."
                  />
                </SimpleGrid>
              </Card>
            )}

            <Stack gap="sm" mt="sm">
              {objectiveData.estrategias?.map((est) => {
                const id = est.Id;
                const isOpen = openStrategyId === id;
                const activeTab = getActiveTab(id);

                const metasCount = est.metas?.length || 0;
                const recursosCount = est.recursos?.length || 0;
                const responsablesCount = est.responsables?.length || 0;

                return (
                  <Card key={id} withBorder radius="md" p="sm">
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" onClick={() => toggleStrategy(id)} style={{ cursor: "pointer" }}>
                        {!isOpen ? <IconChevronDown /> : <IconChevronUp />}
                        <Box>
                        <Text fw={600} truncate="end" maw={rem(550)}>
                          Estrategia - {est.descripcion || "[Sin descripción]"}
                        </Text>
                        <Text c="dimmed" fw={500} size="sm">
                          {metasCount} Metas • {recursosCount} Recursos • {responsablesCount} Responsables
                        </Text>
                        </Box>
                      </Group>
                      {lock.editable && (
                        <ActionIcon 
                          variant="default" 
                          title="Eliminar" 
                          onClick={() => handleDeleteStrategy(id)}
                          loading={is.strategy.deleting(id)} disabled={is.strategy.saving(id) || obj.deleting || obj.completing}  
                        >
                          <IconTrash size={20} />
                        </ActionIcon>
                      )}
                    </Group>

                    <Collapse in={isOpen}>
                      <Stack p="sm" gap="sm">
                        <Box>
                          {lock.completed ? (
                            <Text>{est.descripcion ?? ''}</Text>
                          ):(
                            <>
                              <Textarea
                                label="Descripción de la Estrategia"
                                placeholder="Describa la estrategia ..."
                                autosize
                                minRows={2}
                                maxRows={4}
                                maxLength={350}
                                value={est.descripcion || ""}
                                onChange={(e) => handleStrategyDescription(id, e.target.value)}
                                disabled={lock.readOnly}
                              />
                              <Text ta="end" c="dimmed" size="sm">
                                {(est.descripcion?.length || 0)}/350
                              </Text>
                            </>
                          )}
                        </Box>

                        <SegmentedControl
                          fullWidth
                          withItemsBorders={false}
                          color="#3590f3ff"
                          data={TABS}
                          value={activeTab}
                          onChange={(tab) => setActiveTab(id, tab)}
                        />

                        {activeTab === 'Metas' && (
                          <Box my="sm">
                            {lock.editable && (
                              <Card withBorder radius="md" p="sm" mb='sm' style={{ borderLeftColor: '#e72166', borderLeftWidth: '4px' }}>
                                <SimpleGrid cols={2}>
                                  <TitleWithDescription
                                    icon={<IconInfoCircle size={16} />}
                                    size="sm"
                                    title="Definición de Meta"
                                    description='Son los resultados concretos, cuantificables y medibles que se espera alcanzar a través de la ejecución de las actividades. Cada actividad puede realizarse a partir de varias metas. Funcionan como estimaciones tanto cuantitativas como cualitativas y deben definirse en términos de cantidad y tiempo.'
                                  />
                                  <TitleWithDescription
                                    icon={<IconBulb size={16} />}
                                    size="sm"
                                    title="Recomendaciones"
                                    description='Deben ser enunciadas en tiempo futuro reflejando los logros esperados. Deben incluir un indicador cuantitativo que permita evaluar su cumplimiento. La calendarización de las metas debe establecerse en plazos de corto, mediano y largo plazo. Se deberá indicar en cada caso qué período abarca el corto mediano y largo plazo para poder hacer el seguimiento de cada acción y sus plazos de ejecución.'
                                  />
                                </SimpleGrid>
                              </Card>
                            )}

                            {renderItems(
                              est.metas ?? [],
                              (meta) => openEditModal(id, 'Metas', meta),
                              (metaId) => handleDeleteGoal(id, metaId),
                              (meta) => `${meta.titulo}`,
                              'Meta',
                              (meta) => `Vencimiento: ${formatDate(meta.fechaVencimiento)}`,
                            )}
                          </Box>
                        )}

                        {activeTab === 'Recursos' && (
                          <Box my="sm">
                            {lock.editable && (
                              <Card withBorder radius="md" p="sm" mb='sm' style={{ borderLeftColor: '#e72166', borderLeftWidth: '4px' }}>
                                <SimpleGrid cols={2}>
                                  <TitleWithDescription
                                    icon={<IconInfoCircle size={16} />}
                                    size="sm"
                                    title="Definición de Recurso"
                                    description="Son los medios que se disponen para el desarrollo de las actividades planificadas. Pueden ser materiales (equipamiento, documentación, espacios físicos), financieros (presupuesto asignado) y humanos (personal responsable del desarrollo de las acciones). "
                                  />
                                  <TitleWithDescription
                                    icon={<IconBulb size={16} />}
                                    size="sm"
                                    title="Recomendaciones"
                                    description="Incluso si no se requieren recursos financieros es fundamental describir los recursos materiales y humanos implicados en su desarrollo. La planificación debe considerar la disponibilidad y factibilidad de los recursos requeridos."
                                  />
                                </SimpleGrid>
                              </Card>
                            )}
                            
                            {renderItems(
                              est.recursos ?? [],
                              (recurso) => openEditModal(id, 'Recursos', recurso),
                              (recursoId) => handleDeleteResource(id, recursoId),
                              (recurso) => recurso.titulo,
                              'Recurso'
                            )}
                          </Box>
                        )}

                        {activeTab === 'Responsables' && (
                          <Box my="sm">
                            {lock.editable && (
                              <Card withBorder radius="md" p="sm" mb='sm' style={{ borderLeftColor: '#e72166', borderLeftWidth: '4px' }}>
                                <SimpleGrid cols={2}>
                                  <TitleWithDescription
                                    icon={<IconInfoCircle size={16} />}
                                    size="sm"
                                    title="Definición de Responsable" 
                                    description="Son los responsables del desarrollo de las actividades y de hacer su seguimiento dentro del ámbito de la facultad o la carrera. Se describen las personas a la que se le encomienda la tarea de diseñar y realizar las actuaciones, ocuparse de su seguimiento, vigilar el cumplimiento de los plazos, etc. "
                                  />
                                  <TitleWithDescription
                                    icon={<IconBulb size={16} />}
                                    size="sm"
                                    title="Recomendaciones"
                                    description="Debe asignarse una persona o equipo responsable para cada actividad. Su función incluye la supervisión del cumplimiento de los plazos y la correcta implementación de las estrategias. Se recomienda distribuir las responsabilidades según las áreas específicas del plan de mejora. El seguimiento continuo del plan es clave para asegurar el logro de los resultados previstos. "
                                  />
                                </SimpleGrid>
                              </Card>
                            )}
                            
                            {renderItems(
                              est.responsables ?? [],
                              (resp) => openEditModal(id, 'Responsables', resp),
                              (respId) => handleDeleteResponsible(id, respId),
                              (resp) => resp.usuarioMail,
                              'Responsable',
                              (resp) => resp.descripcion && resp.descripcion.length > 35 
                                ? resp.descripcion.slice(0, 100) + '...' 
                                : resp.descripcion
                            )}
                          </Box>
                        )}
                        {lock.editable && (
                          <Button
                            fullWidth
                            color="#e72166"
                            variant="outline"
                            onClick={() => openCreateModal(id, activeTab)}
                            leftSection={<IconPlus size={18} />}
                          >
                            Agregar elemento
                          </Button>
                        )}
                      </Stack>
                    </Collapse>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </Collapse>

      <ModalEditorGoal
        opened={strategyModal.open && strategyModal.tab === 'Metas'}
        onClose={handleModalClose}
        onSave={handleSaveStrategyElement}
        goalData={strategyModal.item}
      />

      <ModalEditorResource
        opened={strategyModal.open && strategyModal.tab === 'Recursos'}
        onClose={handleModalClose}
        onSave={handleSaveStrategyElement}
        resourceData={strategyModal.item}
      />

      <ModalEditorResponsible
        opened={strategyModal.open && strategyModal.tab === 'Responsables'}
        onClose={handleModalClose}
        onSave={handleSaveStrategyElement}
        responsibleData={strategyModal.item}
      />

      <ModalDialog
        opened={completeOpen}
        onClose={() => setCompleteOpen(false)}
        onConfirm={() => handleConfirmComplete(objectiveData)}
        title="Completar objetivo"
        description={ <Text span> Al completar el objetivo, no podrá editarlo nuevamente. Si su intencion es un guardado parcial lo podra hacer haciendo click en <b>"Guardado Parcial"</b><br/><br/> ¿Desea continuar?</Text>}
        confirmColor={"green"}
      />
      <ModalDialog
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDelete(objective.Id)}
        title="Eliminar objetivo"
        description={<Text span>Al eliminar el objetivo, no podrá recuperar la informcion cargada, incluyendo estrategias, metas, recursos y responsables asociados a este objetivo.<br/><br/> ¿Desea continuar?</Text>}
        confirmColor={"red"}
      />
    </Card>
  );
};

export default ObjectiveCard;
